import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import { State } from './types';

type TrackedMutation = {
	contextJSON: string;
	id: string;
	name: string;
	queryJSON: string;
	variablesJSON: string;
};

// ACTIONS
const ADD_TRACKED_MUTATION = 'ADD_TRACKED_MUTATION';
const REMOVE_TRACKED_MUTATION = 'REMOVE_TRACKED_MUTATION';
const SYNC_TRACKED_MUTATIONS = 'SYNC_TRACKED_MUTATIONS';

type AddTrackedMutationAction = {
	payload: TrackedMutation;
	type: typeof ADD_TRACKED_MUTATION;
};

type RemoveTrackedMutationAction = {
	payload: string;
	type: typeof REMOVE_TRACKED_MUTATION;
};

type SyncTrackedMutationsAction = {
	payload: boolean;
	type: typeof SYNC_TRACKED_MUTATIONS;
};

export type TrackedMutationsActionTypes =
	| AddTrackedMutationAction
	| RemoveTrackedMutationAction
	| SyncTrackedMutationsAction;

// ACTION CREATORS
export const addTrackedMutation = (
	payload: TrackedMutation
): TrackedMutationsActionTypes => ({
	payload,
	type: ADD_TRACKED_MUTATION,
});

export const removeTrackedMutation = (
	payload: string
): TrackedMutationsActionTypes => ({
	payload,
	type: REMOVE_TRACKED_MUTATION,
});

export const syncTrackedMutations = (
	payload: boolean
): TrackedMutationsActionTypes => ({
	payload,
	type: SYNC_TRACKED_MUTATIONS,
});

// STATE
type TrackedMutationsByIdState = {
	[key: string]: TrackedMutation;
};
type TrackedMutationsIdsState = string[];
type SyncTrackedMutationsState = boolean;

export type TrackedMutationsState = {
	byId: TrackedMutationsByIdState;
	ids: TrackedMutationsIdsState;
	syncing: SyncTrackedMutationsState;
};

// REDUCERS
const syncing = (
	state: SyncTrackedMutationsState = false,
	action: TrackedMutationsActionTypes
): SyncTrackedMutationsState => {
	switch (action.type) {
		case SYNC_TRACKED_MUTATIONS: {
			return action.payload;
		}
		default: {
			return state;
		}
	}
};

const byId = (
	state: TrackedMutationsByIdState = {},
	action: TrackedMutationsActionTypes
): TrackedMutationsByIdState => {
	let newState: TrackedMutationsByIdState;
	switch (action.type) {
		case ADD_TRACKED_MUTATION:
			return {
				...state,
				[action.payload.id]: action.payload,
			};
		case REMOVE_TRACKED_MUTATION:
			newState = {
				...state,
			};
			delete newState[action.payload];
			return newState;
		default:
			return state;
	}
};

const ids = (
	state: TrackedMutationsIdsState = [],
	action: TrackedMutationsActionTypes
): TrackedMutationsIdsState => {
	switch (action.type) {
		case ADD_TRACKED_MUTATION:
			return [...state, action.payload.id];
		case REMOVE_TRACKED_MUTATION:
			return state.filter((id) => id !== action.payload);

		default:
			return state;
	}
};

export default combineReducers({
	byId,
	ids,
	syncing,
});

// SELECTORS
const getTrackedMutationsIds = (state: State): TrackedMutationsIdsState =>
	state.trackedMutations.ids;
const getTrackedMutationsById = (state: State): TrackedMutationsByIdState =>
	state.trackedMutations.byId;
export const getSyncingTrackedMutations = (state: State): boolean =>
	state.trackedMutations.syncing;

export const getTrackedMutations = createSelector(
	getTrackedMutationsIds,
	getTrackedMutationsById,
	(pIds, pById) => pIds.map((o) => pById[o])
);
